// DiscoveryOverlay (§5.1) — full-screen celebration when a NEW element is forged.
// tier-colored burst + rotating rays + pulsing glow behind a floating animated
// orb, the element name, tier chip, a one-line fact, plus Share + Keep forging.
// Anchored to the shell root (rendered via Screen's `overlay` prop) so it stays
// centered even when the tray is scrolled (§4.2).
import React from "react";
import ElementOrb, { tierColor, tierName } from "../ui/ElementOrb";
import DiscoveryBurst from "./DiscoveryBurst";
import { ELEMENT_DEFS } from "../elementDefinitions";
import { getLocalFunFact } from "../../services/funFactService";
import type { Praise } from "../../game/praise";

interface DiscoveryOverlayProps {
  elementId: string | null;
  ingredients?: string[];
  praise?: Praise | null;
  onShare: () => void;
  onClose: () => void;
}

const DiscoveryOverlay: React.FC<DiscoveryOverlayProps> = ({
  elementId,
  ingredients = [],
  praise,
  onShare,
  onClose,
}) => {
  if (!elementId) return null;
  const def = ELEMENT_DEFS[elementId];
  if (!def) return null;

  const ring = tierColor(def.tier);
  const fact =
    getLocalFunFact(elementId, ingredients) ||
    `A ${tierName(def.tier).toLowerCase()} element joins your codex.`;

  return (
    <div
      onClick={onClose}
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 40,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "rgba(8,9,20,0.82)",
        backdropFilter: "blur(6px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          overflow: "hidden",
          width: "100%",
          maxWidth: 320,
          padding: "28px 24px",
          borderRadius: 24,
          textAlign: "center",
          background: "linear-gradient(160deg,var(--hero-1),var(--hero-2))",
          border: "1px solid rgba(101,163,13,0.4)",
          boxShadow: "0 30px 80px rgba(0,0,0,0.5)",
          animation: "mcPop .4s ease",
        }}
      >
        <DiscoveryBurst color={ring} />

        {praise && (
          <div
            style={{
              position: "relative",
              zIndex: 3,
              marginBottom: 8,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              animation: "praisePopIn .5s cubic-bezier(.2,.8,.2,1) both",
            }}
          >
            <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontWeight: 700, fontSize: 18, color: praise.color }}>
              {praise.word}
            </span>
            {praise.mult > 1 && (
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 999,
                  color: "#fff",
                  background: praise.color,
                }}
              >
                ×{praise.mult}
              </span>
            )}
          </div>
        )}

        <div style={{ position: "relative", zIndex: 3, fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#F5B740" }}>
          New Discovery
        </div>

        <div style={{ position: "relative", zIndex: 2, margin: "18px auto 0", animation: "mcFloat 3s ease-in-out infinite", display: "inline-flex" }}>
          <ElementOrb id={elementId} size={96} withRing />
        </div>

        <div style={{ marginTop: 18, fontFamily: "'Space Grotesk',sans-serif", fontSize: 26, fontWeight: 700 }}>{def.name}</div>

        <div style={{ marginTop: 8, display: "inline-flex" }}>
          <span style={{ fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 999, color: ring, background: `${ring}22`, border: `1px solid ${ring}55` }}>
            {tierName(def.tier)}
          </span>
        </div>

        <div style={{ marginTop: 14, fontSize: 13, color: "var(--text-2)", lineHeight: 1.5 }}>{fact}</div>

        <div style={{ position: "relative", zIndex: 3, marginTop: 20, display: "flex", gap: 10 }}>
          <button
            className="mc-tap"
            onClick={onShare}
            aria-label="Share discovery"
            style={{
              flex: "0 0 auto",
              width: 52,
              minHeight: 48,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1px solid rgba(255,255,255,0.18)",
              borderRadius: 14,
              background: "rgba(255,255,255,0.06)",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4" />
            </svg>
          </button>
          <button
            className="mc-tap"
            onClick={onClose}
            style={{ flex: 1, minHeight: 48, border: "none", borderRadius: 14, background: "#65A30D", color: "#fff", fontFamily: "'DM Sans',sans-serif", fontSize: 15, fontWeight: 700, cursor: "pointer" }}
          >
            Keep forging
          </button>
        </div>
      </div>
    </div>
  );
};

export default DiscoveryOverlay;
